<template lang="pug">
  .customer-info
    basic-customer-info(:customer-record="customerRecord"
                        :customer-record-link="customerRecordLink"
                        :profile="profile"
                        :primaryAgent="primaryAgent"
                        :profileMapping="profileMapping"
                        :entities="entities"
                        :customerSegment="customerSegment"
                        :customer="customer"
                        :disable="disable"
                        :includeDetails="includeDetails"
                        :channel="channel"
                        @update-field="updateField")
    template(v-if="showDeviceInfo")
      br
      device-info(:messages="messages")
      br
    template(v-if="showFeedbackInfo")
      .bottom-line
      br
      feedback-info(:info="lastFeedbackInfo")
      br
    template(v-if="showCelebrationInfo && channel === 'webchat' && canView('/conversations/right_panel/configure_celebration')")
      .bottom-line
      br
      celebration-info(:celebrationSettings="celebrationSettings")
      br
    template(v-if="canView('/conversations/right_panel/mute_customer') && showMute && showMuteSection")
      .bottom-line
      br
      mute-customer(:customer="customer" @mute-toggled="MuteToggled")
      br
    template(v-if="canView('/conversations/right_panel/remove_customer_profile_image') && profileImage")
      .bottom-line
      br
      remove-profile-image(:customer="customer")
      br
</template>

<script>
import get from 'lodash/get';
import set from 'lodash/set';
import assign from 'lodash/assign';
import isEmpty from 'lodash/isEmpty';
import BasicCustomerInfo from './BasicCustomerInfo.vue';
import DeviceInfo from './DeviceInfo.vue';
import FeedbackInfo from './FeedbackInfo.vue';
import CelebrationInfo from './CelebrationInfo.vue';
import MuteCustomer from './MuteCustomer.vue';
import RemoveProfileImage from './RemoveProfileImage.vue';
import { formatCustomerSegmentsForDisplay } from '@/libs/customerSegments';
import { PROFILE_EDITOR_TYPE } from '@/constants/settings';
import { mapState } from 'vuex';

function resolveFieldName(item) {
  // Note: it is returning 'name' for CONCAT_STRING as this is an exceptional field that are
  // combined with first_name and the last name. We might need a change in future for this.
  return (item.type === PROFILE_EDITOR_TYPE.CONCAT_STRING && !item.field) ? 'name' : item.field;
}

export default {
  components: {
    BasicCustomerInfo,
    DeviceInfo,
    FeedbackInfo,
    CelebrationInfo,
    MuteCustomer,
    RemoveProfileImage,
  },
  data() {
    return {
      // A container that needs to store values temporarily.
      cachedProfile: {},
      profileImage: null,
    };
  },
  computed: {
    profile() {
      return assign({}, this.customer.profile, this.cachedProfile);
    },

    showMuteSection() {
      return get(this.conversation, 'channel', '') === 'webchat';
    },

    customerRecord() {
      return this.profile.external_id || '';
    },
    customerRecordLink() {
      const externalLink = get(this.tenant, 'external_customer_link');
      if (externalLink) {
        // The externalLink may contains link with template which has
        // '{{}}' as a replacing location indicator.
        // ex) https://test.com/camp/default.aspx?CustomerId={{}} will be replaced to
        // https://test.com/camp/default.aspx?CustomerId=1234 if customerRecord is 1234
        return externalLink.replace('{{}}', this.customerRecord);
      }
      return '';
    },
    primaryAgent() {
      const firstName = get(this, 'conversation.primaryAgent.profile.first_name', '');
      const lastName = get(this, 'conversation.primaryAgent.profile.last_name', '');
      return `${firstName} ${lastName}`.trim();
    },
    entities() {
      const entities = this.conversation.ai_state ? this.conversation.ai_state.entities : null;
      return Array.of.apply(null, entities);
    },
    editables() {
      return get(this.sidePanelConfig, 'profile_mapping', [])
        .filter(mapping => mapping.editable)
        .map(resolveFieldName);
    },
    profileMapping() {
      return get(this.sidePanelConfig, 'profile_mapping', []);
    },
    customerSegment() {
      return formatCustomerSegmentsForDisplay(get(this, 'customer.customer_segments', ''));
    },

    ...mapState({
      featureFlags: state => state.featureFlags.FEATURE_FLAGS,
    }),
  },
  props: {
    channel: {
      type: String,
    },
    conversation: {
      type: Object,
    },
    customer: {
      type: Object,
    },
    tenant: {
      type: Object,
    },
    sidePanelConfig: {
      type: Object,
    },
    disable: {
      type: Boolean,
      default: false,
    },
    includeDetails: {
      type: Boolean,
      default: true,
    },
    messages: {
      type: Array,
      default: () => [],
    },
    lastFeedbackInfo: {
      type: Object,
      default: null,
    },
    showDeviceInfo: {
      type: Boolean,
      default: true,
    },
    showFeedbackInfo: {
      type: Boolean,
      default: true,
    },
    showCelebrationInfo: {
      type: Boolean,
      default: true,
    },
    celebrationSettings: {
      type: Object,
      default: () => ({}),
    },
    showMute: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update-profile', 'mute-toggled'],
  watch: {
    customer: {
      handler(newCustomer) {
        // invalid cache whenever new customer config is set.
        this.cachedProfile = {};
        this.profileImage = newCustomer.profile_image;
      },
      deep: true,
      immediate: true,
    },
    entities(newEntities) {
      this.populateEmptyProfileFieldsFromEntities(newEntities);
    },
  },
  methods: {
    updateField({ mapping, value }) {
      // If editable field is set to be false, the update should be always local.
      // So we store it only in cachedProfile which can disappear if this object
      // gets updated.
      if (!mapping.editable && mapping.field) {
        set(this.cachedProfile, mapping.field, value);
        return;
      }


      if (mapping.type === PROFILE_EDITOR_TYPE.CONCAT_STRING) {
        const changedProfile = this.concatStringToProfile(mapping.fields, value);
        this.updateProfile({ ...this.profile, ...changedProfile });
      } else {
        this.updateProfile({ ...this.profile, ...set({}, mapping.field, value) });
      }
    },

    MuteToggled(muteMsg) {
      this.$emit('mute-toggled', muteMsg);
    },

    concatStringToProfile(subMappings, value) {
      const split = value.split(' ');
      const result = {};

      subMappings.forEach((mapping, index) => {
        if (subMappings.length === index + 1 && split.length > subMappings.length) {
          result[mapping.field] = split.slice(index).join(' ');
        } else {
          result[mapping.field] = (split.length <= index) ? '' : split[index];
        }
      });

      return result;
    },

    updateProfile(profile) {
      this.$emit('update-profile', profile);
    },

    populateEmptyProfileFieldsFromEntities(entities) {
      if (isEmpty(entities)) return;

      const entityMap = {};
      entities.forEach(e => { entityMap[e.name] = e });

      const updates = {};

      const nameEntity = entityMap['customer_name'];
      if (nameEntity) {
        if (!get(this.profile, 'first_name')) {
          updates.first_name = get(nameEntity, 'extras.first_name') || nameEntity.resolved_value.split(' ')[0] || '';
        }
        if (!get(this.profile, 'last_name')) {
          const fromExtras = get(nameEntity, 'extras.last_name');
          if (fromExtras) {
            updates.last_name = fromExtras;
          } else {
            const parts = nameEntity.resolved_value.split(' ');
            updates.last_name = parts.length > 1 ? parts.slice(1).join(' ') : '';
          }
        }
      }

      const firstNameEntity = entityMap['customer_first_name'] || entityMap['customer.first_name'];
      if (firstNameEntity && !get(this.profile, 'first_name') && !updates.first_name) {
        updates.first_name = firstNameEntity.resolved_value;
      }

      const lastNameEntity = entityMap['customer_last_name'] || entityMap['customer.last_name'];
      if (lastNameEntity && !get(this.profile, 'last_name') && !updates.last_name) {
        updates.last_name = lastNameEntity.resolved_value;
      }

      const emailEntity = entityMap['customer_email'] || entityMap['customer.email'];
      if (emailEntity && !get(this.profile, 'email')) {
        updates.email = emailEntity.resolved_value;
      }

      const phoneEntity = entityMap['customer_primary_phone_number'] || entityMap['customer_phone'] || entityMap['customer.phone'];
      if (phoneEntity && !get(this.profile, 'phone')) {
        updates.phone = phoneEntity.resolved_value;
      }

      if (Object.keys(updates).length) {
        this.updateProfile({ ...this.profile, ...updates });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.bottom-line {
  border-bottom: 1px solid #BFCBD9;
}
</style>
